'use client';

import { useState, useMemo, useEffect } from 'react';
import styles from './PricingCalculator.module.css';
import { useLanguage } from '@/contexts/LanguageContext'; // <-- IMPORT DO IDIOMA
import { 
  Check, Plus, Minus, Send, MessageSquare, Users, Cloud, 
  Zap, Target, DollarSign, ShoppingCart, Truck, Wrench, Search,
  AlertTriangle, Crown, Sparkles, Settings
} from 'lucide-react';

type PresetPack = {
  id: string;
  icon: React.ElementType;
  baseModules?: string[];
  bonusModules?: string[];
  fixedAddons?: Record<string, number>;
};

// Removemos os nomes físicos. Vamos puxar tudo do dicionário via ID.
const defaultPrices = {
  modules: [
    { id: 'sales', price: 100, icon: ShoppingCart },
    { id: 'finance', price: 100, icon: DollarSign },
    { id: 'aesir_chat', price: 100, icon: MessageSquare },
    { id: 'campaigns', price: 100, icon: Target },
    { id: 'crm', price: 100, icon: Users },
    { id: 'automations', price: 100, icon: Zap },
    { id: 'lead_search', price: 10, icon: Search },
    { id: 'hr', price: 40, icon: Users },
    { id: 'logistics', price: 40, icon: Truck },
    { id: 'service_orders', price: 40, icon: Wrench },
    { id: 'aesir_cloud', price: 100, icon: Cloud },
  ],
  addons: [
    { id: 'user_license', price: 5, step: 1, min: 1 },
    { id: 'extra_instance', price: 10, step: 1, min: 0 },
    { id: 'official_api', price: 20, step: 1, min: 0 },
    { id: 'agent_license', price: 5, step: 1, min: 0 },
    { id: 'lead_credits', price: 1, step: 100, min: 0 },
  ]
};

const PRESET_PACKS: Record<string, PresetPack> = {
  starter: { id: 'starter', icon: Target, baseModules: ['campaigns'], bonusModules: [], fixedAddons: { 'user_license': 1, 'extra_instance': 1 } },
  essential: { id: 'essential', icon: Sparkles, baseModules: ['sales', 'campaigns'], bonusModules: ['aesir_chat', 'crm'], fixedAddons: { 'user_license': 3, 'extra_instance': 2 } },
  premium: { id: 'premium', icon: Crown, baseModules: ['sales', 'finance', 'aesir_chat', 'campaigns', 'crm'], bonusModules: ['automations', 'lead_search', 'hr'], fixedAddons: { 'user_license': 5, 'extra_instance': 5 } },
  custom: { id: 'custom', icon: Settings }
};

export function PricingCalculator() {
  const { t, lang } = useLanguage(); // MOTOR DE TRADUÇÃO

  const [activePack, setActivePack] = useState<string>('essential');
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
  const [addons, setAddons] = useState<Record<string, number>>({});

  // ==========================================
  // LÓGICA DE CONVERSÃO DE MOEDAS (R$ vs €)
  // ==========================================
  const currencyCode = t('currency.code') !== 'currency.code' ? t('currency.code') : 'BRL';
  const currencyRate = parseFloat(t('currency.rate') !== 'currency.rate' ? t('currency.rate') : '1');

  const formatPrice = (basePrice: number) => {
    const convertedPrice = basePrice * currencyRate;
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
    }).format(convertedPrice);
  };

  useEffect(() => {
    if (activePack === 'custom') return;
    const packData = PRESET_PACKS[activePack];
    if (packData && packData.baseModules && packData.bonusModules) {
      const allMods = new Set([...packData.baseModules, ...packData.bonusModules]);
      setSelectedModules(allMods);
      setAddons({ ...packData.fixedAddons });
    }
  }, [activePack]);

  const bonusModulesList = activePack === 'custom' ? [] : (PRESET_PACKS[activePack].bonusModules || []);

  const toggleModule = (id: string) => {
    if (activePack !== 'custom') {
      const packData = PRESET_PACKS[activePack];
      if (packData.baseModules?.includes(id) || packData.bonusModules?.includes(id)) return; 
    }
    const newSelected = new Set(selectedModules);
    if (newSelected.has(id)) newSelected.delete(id); else newSelected.add(id);
    setSelectedModules(newSelected);
  };

  const updateAddon = (id: string, amount: number, min: number = 0) => {
    setAddons(prev => {
      let realMin = min;
      if (activePack !== 'custom') {
        const packData = PRESET_PACKS[activePack];
        if (packData.fixedAddons && packData.fixedAddons[id] !== undefined) realMin = packData.fixedAddons[id];
      }
      const current = prev[id] || 0;
      return { ...prev, [id]: Math.max(realMin, current + amount) };
    });
  };

  const requiresWhatsApp = selectedModules.has('aesir_chat') || selectedModules.has('campaigns');
  const hasWhatsAppInstance = (addons['extra_instance'] || 0) > 0 || (addons['official_api'] || 0) > 0;
  
  const warnings = useMemo(() => {
    const alerts = [];
    if (selectedModules.size === 0) alerts.push(t('pricing.warnMinModules'));
    if ((addons['user_license'] || 0) < 1) alerts.push(t('pricing.warnMinUsers'));
    if (requiresWhatsApp && !hasWhatsAppInstance) alerts.push(t('pricing.warnWhatsapp'));
    return alerts;
  }, [selectedModules, addons, requiresWhatsApp, hasWhatsAppInstance, t]);

  const isValid = warnings.length === 0;

  const calculateTotals = () => {
    let totalToPay = 0; let totalSaved = 0;
    defaultPrices.modules.forEach(m => {
      if (selectedModules.has(m.id)) {
        if (bonusModulesList.includes(m.id)) totalSaved += m.price;
        else totalToPay += m.price;
      }
    });
    defaultPrices.addons.forEach(a => {
      const qty = addons[a.id] || 0;
      totalToPay += (qty * a.price);
    });
    return { totalToPay, totalSaved };
  };

  const { totalToPay, totalSaved } = calculateTotals();

  // Whatsapp Botão
  const handleWhatsAppRedirect = () => {
    if (!isValid) return;
    const packName = t(`pricing.packs.${activePack}.name`);
    const modNames = defaultPrices.modules.filter(m => selectedModules.has(m.id) && !bonusModulesList.includes(m.id)).map(m => t(`pricing.modules.${m.id}`)).join(', ');
    const bonusNames = defaultPrices.modules.filter(m => bonusModulesList.includes(m.id)).map(m => t(`pricing.modules.${m.id}`)).join(', ');
    const addNames = defaultPrices.addons.filter(a => addons[a.id] > 0).map(a => `${addons[a.id]}x ${t(`pricing.addons.${a.id}`)}`).join(', ');
    
    // Puxa o template traduzido e substitui as chaves
    const template = t('pricing.whatsappText');
    const text = template
      .replace('{pack}', packName)
      .replace('{mods}', modNames || 'Nenhum')
      .replace('{bonus}', bonusNames || 'Nenhum')
      .replace('{adds}', addNames || 'Nenhum')
      .replace('{total}', formatPrice(totalToPay));

    window.open(`https://api.whatsapp.com/send?phone=5511983493522&text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div>
      {/* ABAS DE PACOTES COM BADGES TRADUZIDOS */}
      <div className={styles.planTabs}>
        {Object.values(PRESET_PACKS).map(pack => {
          const badgeText = t(`pricing.packs.${pack.id}.badge`);
          const hasBadge = badgeText && badgeText !== `pricing.packs.${pack.id}.badge`;
          return (
            <button 
              key={pack.id} 
              className={`${styles.tabBtn} ${activePack === pack.id ? styles.active : ''}`}
              onClick={() => setActivePack(pack.id)}
            >
              {hasBadge && (
                <span className={`${styles.tabBadge} ${pack.id === 'essential' ? styles.badgeGreen : styles.badgeOrange}`}>
                  {badgeText}
                </span>
              )}
              <pack.icon size={18} />
              {t(`pricing.packs.${pack.id}.name`)}
            </button>
          )
        })}
      </div>

      <div className={styles.calculatorWrapper}>
        <div className={styles.modulesSection}>
          
          <div className={styles.sectionHeader}>
            <h3>{t('pricing.title1')}</h3>
            <p>{activePack === 'custom' ? t('pricing.desc1Custom') : t('pricing.desc1Pack')}</p>
          </div>
          
          <div className={styles.grid}>
            {defaultPrices.modules.map(mod => {
              const Icon = mod.icon;
              const isSelected = selectedModules.has(mod.id);
              const isBonus = bonusModulesList.includes(mod.id);
              
              let cardClass = styles.moduleCard;
              if (isSelected) cardClass += ` ${styles.active}`;
              if (isBonus) cardClass += ` ${styles.freeBonus}`;
              else if (activePack !== 'custom' && isSelected) cardClass += ` ${styles.lockedPack}`;

              return (
                <div key={mod.id} className={cardClass} onClick={() => toggleModule(mod.id)}>
                  <div className={styles.cardHeader}>
                    <div className={styles.iconWrapper}><Icon size={20} /></div>
                    <div className={styles.checkbox}>{isSelected && <Check size={14} strokeWidth={4} />}</div>
                  </div>
                  <div className={styles.modInfo}>
                    <span className={styles.modName}>{t(`pricing.modules.${mod.id}`)}</span>
                    <div className={styles.priceWrapper}>
                      {isBonus ? (
                        <>
                          <span className={styles.priceStrikethrough}>{formatPrice(mod.price)}</span>
                          <span className={styles.priceFree}>{t('pricing.free')}</span>
                        </>
                      ) : (
                        <span className={styles.modPrice}>{formatPrice(mod.price)}{t('pricing.month')}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.sectionHeader} style={{ marginTop: '3rem' }}>
            <h3>{t('pricing.title2')}</h3>
            <p>{t('pricing.desc2')}</p>
          </div>
          
          <div className={styles.addonsList}>
            {defaultPrices.addons.map(addon => {
              const isMissingInstance = requiresWhatsApp && !hasWhatsAppInstance && (addon.id === 'extra_instance' || addon.id === 'official_api');
              
              let currentMin = addon.min;
              if (activePack !== 'custom') {
                const packData = PRESET_PACKS[activePack];
                if (packData.fixedAddons && packData.fixedAddons[addon.id] !== undefined) {
                  currentMin = packData.fixedAddons[addon.id];
                }
              }

              const isMinusDisabled = (addons[addon.id] || 0) <= currentMin;

              return (
                <div key={addon.id} className={`${styles.addonRow} ${isMissingInstance ? styles.requiredHighlight : ''}`}>
                  <div className={styles.addonInfo}>
                    <span className={styles.addonName}>{t(`pricing.addons.${addon.id}`)}</span>
                    <span className={styles.addonPrice}>+ {formatPrice(addon.price)} {addon.step > 1 ? `(${t('pricing.packOf')} ${addon.step})` : t('pricing.each')}</span>
                  </div>
                  <div className={styles.counter}>
                    <button onClick={() => updateAddon(addon.id, -1, currentMin)} disabled={isMinusDisabled}>
                      <Minus size={16} />
                    </button>
                    <span>{addons[addon.id] || 0}</span>
                    <button onClick={() => updateAddon(addon.id, 1, currentMin)}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* PAINEL RESUMO (DIREITA) */}
        <div className={styles.summarySection}>
          <div className={styles.summarySticky}>
            <div className={styles.summaryHeader}>
              <h4>{t('pricing.summaryTitle')}</h4>
            </div>
            
            <div className={styles.summaryBody}>
              <ul className={styles.summaryList}>
                {defaultPrices.modules.filter(m => selectedModules.has(m.id) && !bonusModulesList.includes(m.id)).map(m => (
                  <li key={m.id}><span>{t(`pricing.modules.${m.id}`)}</span> <span>{formatPrice(m.price)}</span></li>
                ))}
                
                {defaultPrices.modules.filter(m => selectedModules.has(m.id) && bonusModulesList.includes(m.id)).map(m => (
                  <li key={m.id} style={{color: '#10b981'}}>
                    <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}><Sparkles size={14}/> {t(`pricing.modules.${m.id}`)}</span> 
                    <span>{t('pricing.bonusLabel')}</span>
                  </li>
                ))}

                {defaultPrices.addons.filter(a => addons[a.id] > 0).map(a => (
                  <li key={a.id} className={styles.addonItem}>
                    <span>{addons[a.id]}x {t(`pricing.addons.${a.id}`)}</span> 
                    <span>{formatPrice(addons[a.id] * a.price)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.summaryFooter}>
              
              {totalSaved > 0 && (
                <div className={styles.savingsBox}>
                  <p>{t('pricing.savingsTitle')}</p>
                  <h5>{formatPrice(totalSaved)} {t('pricing.month')}</h5>
                </div>
              )}

              {warnings.length > 0 && (
                <div className={styles.validationBox}>
                  <div className={styles.validationHeader}>
                    <AlertTriangle size={16} /><span>{t('pricing.warningsTitle')}</span>
                  </div>
                  <ul>{warnings.map((warn, i) => <li key={i}>{warn}</li>)}</ul>
                </div>
              )}

              <div className={styles.totalRow}>
                <span>{t('pricing.totalLabel')}</span>
                <span className={styles.totalPrice}>{formatPrice(totalToPay)}</span>
              </div>
              
              <button className={styles.ctaButton} onClick={handleWhatsAppRedirect} disabled={!isValid}>
                <Send size={18} /> {t('pricing.cta')}
              </button>
              <p className={styles.disclaimer}>{t('pricing.disclaimer')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}